import { HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { AppApis } from "@app/core/APIs/app-apis";
import { CrudApiOptions, CrudApiService, GenResp } from "@template-ui/services/crud-api.service";
import { catchError, map, Observable, of } from "rxjs";
import {
  toWorkflowRegistryFailure,
  toWorkflowRegistrySuccess,
  WorkflowDefinitionRegistryQuery,
  WorkflowDefinitionVersionDetail,
  WorkflowDefinitionVersionSummary,
  WorkflowAlertEvaluation,
  WorkflowAlertRule,
  WorkflowAlertRulePreviewRequest,
  WorkflowAlertRuleUpsertRequest,
  WorkflowDecisionRecordPage,
  WorkflowDecisionRecordQuery,
  WorkflowRegistryDiagnosticResult,
} from "./platform-workflow-registry-diagnostics.models";

const requestHeaders = new HttpHeaders({
  "Content-Type": "application/json",
  Accept: "application/json, text/plain, */*",
  "skip-error-interceptor": "yes",
  "skip-loading-interceptor": "yes",
});

export const platformWorkflowRegistryDiagnosticsApiOptions: Partial<CrudApiOptions<"body", "json">> = {
  retries: 0,
  feedbackOptions: {
    successMode: "none",
    errorMode: "none",
  },
  getOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
  postOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
};

@Injectable({ providedIn: "root" })
export class PlatformWorkflowRegistryDiagnosticsService {
  private readonly apis = inject(AppApis);
  private readonly crudService = inject(CrudApiService);

  listDefinitions(
    query: WorkflowDefinitionRegistryQuery = {},
  ): Observable<WorkflowRegistryDiagnosticResult<WorkflowDefinitionVersionSummary[]>> {
    return this.getDiagnostic(
      this.apis.core.manual.getPlatformWorkflowDefinitions(query),
    );
  }

  getDefinition(
    workflowCode: string,
    definitionVersionCode: string,
  ): Observable<WorkflowRegistryDiagnosticResult<WorkflowDefinitionVersionDetail>> {
    return this.getDiagnostic(
      this.apis.core.manual.getPlatformWorkflowDefinition(workflowCode, definitionVersionCode),
    );
  }

  listAlertRules(
    query: { eventTypeCode?: string; workflowCode?: string } = {},
  ): Observable<WorkflowRegistryDiagnosticResult<WorkflowAlertRule[]>> {
    return this.getDiagnostic(
      this.apis.core.manual.getPlatformWorkflowAlertRules(query),
    );
  }

  upsertAlertRule(
    request: WorkflowAlertRuleUpsertRequest,
  ): Observable<WorkflowRegistryDiagnosticResult<WorkflowAlertRule>> {
    return this.postDiagnostic(
      this.apis.core.manual.postPlatformWorkflowAlertRule(),
      request,
    );
  }

  previewAlertRules(
    request: WorkflowAlertRulePreviewRequest,
  ): Observable<WorkflowRegistryDiagnosticResult<WorkflowAlertEvaluation[]>> {
    return this.postDiagnostic(
      this.apis.core.manual.postPlatformWorkflowAlertRulePreview(),
      request,
    );
  }

  listDecisionRecords(
    query: WorkflowDecisionRecordQuery = {},
  ): Observable<WorkflowRegistryDiagnosticResult<WorkflowDecisionRecordPage>> {
    return this.getDiagnostic(
      this.apis.core.manual.getPlatformWorkflowDecisionRecords(query),
    );
  }

  private getDiagnostic<T>(url: string): Observable<WorkflowRegistryDiagnosticResult<T>> {
    return this.crudService.getbyUrl<T>(url, platformWorkflowRegistryDiagnosticsApiOptions).pipe(
      map((response) => toWorkflowRegistrySuccess(unwrapResponse(response))),
      catchError((error: unknown) => of(toWorkflowRegistryFailure<T>(error))),
    );
  }

  private postDiagnostic<T>(url: string, body: unknown): Observable<WorkflowRegistryDiagnosticResult<T>> {
    return this.crudService.postByUrl<T>(url, body, platformWorkflowRegistryDiagnosticsApiOptions).pipe(
      map((response) => toWorkflowRegistrySuccess(unwrapResponse(response))),
      catchError((error: unknown) => of(toWorkflowRegistryFailure<T>(error))),
    );
  }
}

function unwrapResponse<T>(response: GenResp<T> | T | undefined | null): T {
  if (response && typeof response === "object" && "data" in response) {
    return (response as GenResp<T>).data;
  }

  return response as T;
}
